import { useEffect, useRef, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Mail, Phone, MapPin, Linkedin, Calendar, ArrowRight, Loader2, CheckCircle2, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import FloatingBulb from "@/components/FloatingBulb";
import { SERVICES } from "@/lib/services";
import { whatsappLink } from "@/lib/contact";
import { Recaptcha, Honeypot, getRecaptchaToken, resetRecaptcha } from "@/components/AntiSpam";
import { useSeo, SITE_URL } from "@/lib/seo";
import { fbTrack } from "@/lib/pixel";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CALENDLY_URL = "https://calendly.com/idea360consulting";

export default function Contatti() {
  const [searchParams] = useSearchParams();
  const presetService = searchParams.get("service") || "";

  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    services: presetService ? [presetService] : [],
    message: "",
    consent: false,
    website: "",
  });
  const captchaId = useRef(null);
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  useSeo({
    title: "Contatti | Idea 360 Consulting — Campania",
    description:
      "Scrivici o prenota una call gratuita di 30 minuti: rispondiamo entro 24 ore con una prima valutazione del tuo progetto. Idea 360 Consulting, Campania.",
    path: "/contatti",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "ContactPage",
      name: "Contatti — Idea 360 Consulting",
      url: `${SITE_URL}/contatti`,
      mainEntity: { "@id": `${SITE_URL}/#localbusiness` },
    },
  });

  useEffect(() => {
    if (presetService) {
      setForm((f) => ({ ...f, services: f.services.includes(presetService) ? f.services : [presetService, ...f.services] }));
    }
  }, [presetService]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  };

  const toggleService = (name) =>
    setForm((f) => ({
      ...f,
      services: f.services.includes(name) ? f.services.filter((s) => s !== name) : [...f.services, name],
    }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;
    if (!form.consent) {
      toast.error("Per inviare la richiesta devi accettare il trattamento dei dati.");
      return;
    }
    setLoading(true);
    try {
      const { services, ...rest } = form;
      const recaptcha_token = getRecaptchaToken(captchaId.current);
      await axios.post(`${API}/contact`, { ...rest, recaptcha_token, service: services.length ? services.join(", ") : "Generale" });
      resetRecaptcha(captchaId.current);
      fbTrack("Lead", { content_name: "Form Contatti", content_category: services.length ? services.join(", ") : "Generale" });
      toast.success("Richiesta inviata. Ti risponderemo entro 24 ore.");
      setSent(true);
      setForm({ name: "", email: "", phone: "", services: [], message: "", consent: false, website: "" });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Errore nell'invio. Riprova tra poco.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <section className="relative">
        <FloatingBulb
          size={32}
          className="absolute top-24 right-[10%] hidden md:block"
          testId="contatti-bulb-1"
        />
        <div className="max-w-7xl mx-auto px-6 lg:px-8 pt-10 pb-10 sm:pt-28 sm:pb-12">
          <p className="eyebrow mb-5">Contatti</p>
          <h1
            className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white max-w-4xl leading-[1.05]"
            style={{ fontFamily: "'Outfit', sans-serif", letterSpacing: "-0.03em" }}
            data-testid="contatti-h1"
          >
            Parlaci della tua <span className="text-[#FACC15]">idea</span>.
          </h1>
          <p className="mt-6 text-white/70 text-lg max-w-3xl leading-relaxed">
            Compila il form o prenota una call gratuita di 30 minuti. Rispondiamo entro 24
            ore con una prima valutazione del tuo progetto. Seguiamo imprese e startup a
            Napoli, Salerno, Caserta, Avellino, Benevento e in tutta Italia.
          </p>
        </div>
      </section>

      <section className="pb-16 sm:pb-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-5 gap-10">
          {/* FORM */}
          <div className="lg:col-span-3 border border-[#1a1a1a] bg-[#0a0a0a] p-7 sm:p-10">
            {sent ? (
              <div className="text-center py-10" data-testid="contact-form-success">
                <CheckCircle2 size={48} className="text-[#FACC15] mx-auto mb-5" strokeWidth={1.5} />
                <h2
                  className="text-2xl text-white font-bold mb-3"
                  style={{ fontFamily: "'Outfit', sans-serif" }}
                >
                  Richiesta inviata.
                </h2>
                <p className="text-white/65 max-w-md mx-auto">
                  Grazie! Abbiamo ricevuto la tua richiesta. Un nostro consulente ti
                  ricontatterà entro 24 ore.
                </p>
                <button
                  type="button"
                  className="btn-outline-yellow mt-8"
                  onClick={() => setSent(false)}
                  data-testid="contact-form-new"
                >
                  Invia un'altra richiesta
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} data-testid="contact-form" noValidate className="relative">
                <Honeypot value={form.website} onChange={handleChange} />
                <p className="eyebrow mb-3">Form di contatto</p>
                <h2
                  className="text-2xl sm:text-3xl text-white font-bold mb-3"
                  style={{ fontFamily: "'Outfit', sans-serif" }}
                  data-testid="contact-form-title"
                >
                  Raccontaci del tuo progetto.
                </h2>
                <p className="text-white/65 text-base mb-7" data-testid="contact-form-subtitle">
                  Ci occuperemo noi di crearlo da zero.
                </p>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="sm:col-span-2">
                    <label className="block text-xs tracking-[0.2em] uppercase text-white/55 mb-2" htmlFor="name">
                      Nome completo *
                    </label>
                    <input
                      id="name"
                      name="name"
                      type="text"
                      required
                      minLength={2}
                      value={form.name}
                      onChange={handleChange}
                      placeholder="Mario Rossi"
                      className="field-input"
                      data-testid="contact-input-name"
                    />
                  </div>

                  <div>
                    <label className="block text-xs tracking-[0.2em] uppercase text-white/55 mb-2" htmlFor="email">
                      Email *
                    </label>
                    <input
                      id="email"
                      name="email"
                      type="email"
                      required
                      value={form.email}
                      onChange={handleChange}
                      placeholder="tu@azienda.it"
                      className="field-input"
                      data-testid="contact-input-email"
                    />
                  </div>

                  <div>
                    <label className="block text-xs tracking-[0.2em] uppercase text-white/55 mb-2" htmlFor="phone">
                      Telefono *
                    </label>
                    <input
                      id="phone"
                      name="phone"
                      type="tel"
                      required
                      value={form.phone}
                      onChange={handleChange}
                      placeholder="+39 333 0000000"
                      className="field-input"
                      data-testid="contact-input-phone"
                    />
                  </div>

                  <div className="sm:col-span-2">
                    <p className="block text-xs tracking-[0.2em] uppercase text-white/55 mb-3" id="services-label">
                      Servizi di interesse <span className="text-white/35 normal-case tracking-normal">(puoi sceglierne più di uno)</span>
                    </p>
                    <div className="flex flex-wrap gap-2" role="group" aria-labelledby="services-label" data-testid="contact-input-service">
                      {SERVICES.map((s) => {
                        const on = form.services.includes(s.name);
                        return (
                          <button
                            key={s.slug}
                            type="button"
                            onClick={() => toggleService(s.name)}
                            aria-pressed={on}
                            className={`text-sm px-4 py-3 min-h-[44px] inline-flex items-center border transition-colors ${
                              on ? "bg-[#FACC15] text-black border-[#FACC15] font-semibold" : "border-[#262626] text-white/75 hover:border-[#FACC15]/60"
                            }`}
                            data-testid={`service-option-${s.slug}`}
                          >
                            {s.name}
                          </button>
                        );
                      })}
                    </div>
                    <p className="text-xs mt-3 text-white/45" data-testid="contact-selected-services">
                      {form.services.length
                        ? `Selezionati: ${form.services.join(", ")}`
                        : "Nessun servizio selezionato: la richiesta sarà generale."}
                    </p>
                    {presetService && (
                      <p className="text-xs text-[#FACC15] mt-2" data-testid="contact-preselected">
                        Servizio pre-selezionato: {presetService}
                      </p>
                    )}
                  </div>

                  <div className="sm:col-span-2">
                    <label className="block text-xs tracking-[0.2em] uppercase text-white/55 mb-2" htmlFor="message">
                      Messaggio *
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      required
                      minLength={5}
                      rows={5}
                      value={form.message}
                      onChange={handleChange}
                      placeholder="Descrivi brevemente la tua attività e cosa ti serve..."
                      className="field-input resize-none"
                      data-testid="contact-input-message"
                    />
                  </div>
                </div>

                <label className="mt-6 flex items-start gap-3 cursor-pointer" data-testid="contact-consent-label">
                  <input
                    type="checkbox"
                    name="consent"
                    checked={form.consent}
                    onChange={(e) => setForm((f) => ({ ...f, consent: e.target.checked }))}
                    required
                    className="mt-0.5 w-5 h-5 shrink-0 accent-[#FACC15] bg-transparent border border-[#333]"
                    data-testid="contact-consent"
                  />
                  <span className="text-xs text-white/55 leading-relaxed">
                    Inviando il form dichiaro di aver letto l'
                    <Link to="/privacy-policy" className="text-[#FACC15] underline underline-offset-2" data-testid="contact-privacy-link">informativa privacy</Link>{" "}
                    e acconsento al trattamento dei miei dati personali ai sensi del Regolamento UE 2016/679 (GDPR) e del D.Lgs. 196/2003, come modificato dal D.Lgs. 101/2018, anche per finalità di marketing (invio di comunicazioni su servizi, bandi e iniziative), e a essere ricontattato da Idea 360 Consulting via email, telefono o WhatsApp. *
                  </span>
                </label>

                <Recaptcha onReady={(id) => (captchaId.current = id)} />

                <button
                  type="submit"
                  disabled={loading || !form.consent}
                  className="btn-yellow mt-7 w-full sm:w-auto inline-flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
                  data-testid="contact-form-submit"
                >
                  {loading ? (
                    <>
                      <Loader2 size={18} className="animate-spin" />
                      Invio in corso...
                    </>
                  ) : (
                    <>
                      Invia richiesta
                      <ArrowRight size={18} />
                    </>
                  )}
                </button>
              </form>
            )}
          </div>

          {/* SIDEBAR */}
          <aside className="lg:col-span-2 space-y-6">
            <div className="border border-[#1a1a1a] bg-[#0c0c0c] p-7">
              <p className="eyebrow mb-4">Prenota un meet</p>
              <h3
                className="text-xl text-white font-semibold mb-3"
                style={{ fontFamily: "'Outfit', sans-serif" }}
              >
                Una call diretta con un nostro consulente.
              </h3>
              <p className="text-white/60 text-sm mb-6 leading-relaxed">
                Scegli un orario che ti è comodo: 30 minuti, gratuiti, per capire come
                possiamo aiutarti.
              </p>
              <a
                href={CALENDLY_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-outline-yellow inline-flex items-center gap-2 w-full justify-center"
                data-testid="calendly-button"
              >
                <Calendar size={18} />
                Prenota un meet
              </a>
            </div>

            <div className="border border-[#1a1a1a] bg-[#0c0c0c] p-7">
              <p className="eyebrow mb-4">Contatti diretti</p>
              <ul className="space-y-4 text-sm">
                <li className="flex items-start gap-3">
                  <Mail size={17} className="text-[#FACC15] mt-0.5" />
                  <a
                    href="mailto:info@idea-360.it"
                    className="tap-target text-white/80 hover:text-[#FACC15] transition-colors"
                    data-testid="contact-info-email"
                  >
                    info@idea-360.it
                  </a>
                </li>
                <li className="flex items-start gap-3">
                  <Phone size={17} className="text-[#FACC15] mt-0.5" />
                  <a
                    href="tel:+393773473022"
                    className="tap-target text-white/80 hover:text-[#FACC15] transition-colors"
                    data-testid="contact-info-phone"
                  >
                    +39 377 347 3022
                  </a>
                </li>
                <li className="flex items-start gap-3">
                  <MessageCircle size={17} className="text-[#25D366] mt-0.5" />
                  <a
                    href={whatsappLink("Ciao, vi scrivo dal sito Idea 360: vorrei parlare con un consulente.")}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="tap-target text-white/80 hover:text-[#25D366] transition-colors"
                    data-testid="contact-info-whatsapp"
                  >
                    Scrivici su WhatsApp
                  </a>
                </li>
                <li className="flex items-start gap-3">
                  <MapPin size={17} className="text-[#FACC15] mt-0.5" />
                  <span className="text-white/80">Campania, Italia</span>
                </li>
                <li className="flex items-start gap-3 pt-2 border-t border-[#1a1a1a]">
                  <Linkedin size={17} className="text-[#FACC15] mt-0.5" />
                  <a
                    href="https://www.linkedin.com/company/idea360consulting"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="tap-target text-white/80 hover:text-[#FACC15] transition-colors"
                    data-testid="contact-info-linkedin"
                  >
                    LinkedIn aziendale
                  </a>
                </li>
              </ul>
            </div>
          </aside>
        </div>
      </section>
    </>
  );
}
